'use client';
import React, { useState } from 'react';
import { Icon } from '@benzinga/core-ui';

import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { faLongArrowRight } from '@fortawesome/pro-light-svg-icons/faLongArrowRight';
import styled from '@benzinga/themetron';
import { FieldType, StyleOption } from './index';
import { emailRegEx } from '../../utils/regexes';
import classnames from 'classnames';
import { runningClientSide } from '@benzinga/utils';

export interface HubspotFormProps {
  hubspotFormId?: string;
  onSubmit?: () => void;
  onSuccess?: (message: string, email?: string) => void;
  redirectUrl?: string;
  extraFields?: FieldType[];
  styleOption?: StyleOption;
  placeholderTitle?: string;
  CustomInput?: React.FC<CustomInputProps>;
}

interface CustomInputProps {
  hubspotFormId: string | undefined;
  placeholder?: string;
  type: React.HTMLInputTypeAttribute | undefined;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSuccess?: (message: string, email?: string) => void;
  onSubmit?: () => void;
}

const HubspotFormWrapper = styled.div`
  &.default {
    width: 100%;
    input.hubspot-input {
      outline: none !important;
      box-shadow: none !important;
    }

    .hubspot-form {
      padding: 0.25rem;
      padding-left: 0.625rem;
      display: inline-flex;
      background-color: rgba(206, 221, 242, 0.1);
      border: solid 1px rgba(184, 203, 229, 0.7);
      border-radius: 0.25rem;
      align-items: center;
      width: 100%;
    }
  }

  &.secondary {
    .hubspot-form {
      position: relative;
      display: flex;
      align-items: center;
      height: 42px;
      background: rgba(242, 247, 255, 0.1);
      border: 1px solid rgba(242, 247, 255, 0.1);
      border-radius: 4px;
      padding: 4px 4px 4px 8px;
      transition-property: background-color, border-color;
      transition-timing-function: ease-in-out;
      transition-duration: 0.25s;
    }

    .hubspot-form-error {
      border-color: #ff4d4d;
      background: #ff4d4d33;
    }

    .hubspot-email-icon {
      color: #ffffff;
    }

    .hubspot-input {
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.01em;
      color: #f2f8ff;
      outline: none !important;
      box-shadow: none !important;

      &::placeholder {
        color: #f2f8ff;
      }
    }

    .hubspot-form-button {
      background-color: #ffffff;

      div {
        color: #3f83f8;
      }
    }

    .error-message-wrapper {
      position: relative;
      width: calc(100% - 16px);
      margin: 0 auto;
      border-radius: 0 0 4px 4px;
      z-index: 1;
      background: #ff4d4d;
      height: 32px;
      max-height: 0;
      transition: 0.25s max-height ease-in-out;
      overflow: hidden;
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      display: flex;
      align-items: center;

      span {
        padding: 8px 12px;
      }
    }

    .error-message-active {
      max-height: 32px;
    }
  }
`;

export const HubspotForm = ({
  CustomInput,
  extraFields,
  hubspotFormId,
  onSuccess,
  placeholderTitle = 'Email',
  redirectUrl,
  styleOption = 'default',
}: HubspotFormProps) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>('');
  const [values, setValues] = useState({});
  const handleSubmit = () => {
    setError(null);
    if (!emailRegEx.test(email)) {
      setError('Invalid email.');
      return;
    }
    let fields: {
      name: string;
      value: string;
    }[] = [];
    if (!extraFields) {
      fields = [
        {
          name: 'email',
          value: email,
        },
      ];
    } else {
      extraFields.forEach(field => {
        fields.push({ name: field.name, value: values[field.name] });
      });
    }
    fetch(`https://api.hsforms.com/submissions/v3/integration/submit/2786381/${hubspotFormId}`, {
      body: JSON.stringify({
        context: {
          pageName: document.title,
          pageUri: runningClientSide() ? window.location.href : undefined,
        },
        fields,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
      .then(res => res.json())
      .then(res => {
        if (res.status === 'error') {
          if (res.errors[0].errorType === 'INVALID_EMAIL') {
            setError('Invalid email.');
          }
          return;
        } else if (res.inlineMessage) {
          if (onSuccess) {
            const event = new CustomEvent('form_submit', { detail: { email, hubspotFormId } });
            window.dispatchEvent(event);
            setEmail('');
            onSuccess(res.inlineMessage, email);
          }
        }

        if (redirectUrl && runningClientSide()) {
          window.open(redirectUrl, '_blank');
        }
      })
      .catch(() => {
        setError('Invalid email');
      });
  };

  if (CustomInput) {
    return (
      <CustomInput
        hubspotFormId={hubspotFormId}
        onChange={e => {
          setError(null);
          setEmail(e.target.value);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            handleSubmit();
          }
        }}
        onSubmit={handleSubmit}
        onSuccess={onSuccess}
        placeholder={placeholderTitle}
        type="email"
        value={email}
      />
    );
  }

  return (
    <HubspotFormWrapper className={classnames('hubspot-form-wrapper', styleOption)}>
      <div className={classnames('hubspot-form', { [`hubspot-form-error`]: error })}>
        {extraFields && extraFields.length ? (
          extraFields.map(field => {
            return field.componentType === 'input' ? (
              <div style={{ alignItems: 'center', display: 'flex' }}>
                <Icon className="text-gray" icon={field.icon} />
                <input
                  aria-label={field.type || 'text'}
                  className="hubspot-input w-full text-sm px-2 leading-none placeholder-gray-600 bg-transparent border-none ring-0"
                  data-hubspot-id={hubspotFormId}
                  onChange={e => {
                    setValues({ ...values, [field.name]: e.target.value });
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                  placeholder={field.placeholder}
                  type={field.type || 'text'}
                  value={values[field.name] || ''}
                />{' '}
              </div>
            ) : (
              <button
                aria-label="Click to submit email"
                className="focus:outline-none py-1.5 px-2 inline-flex items-center justify-center bg-yellow-500 text-white rounded"
                onClick={handleSubmit}
                type="button"
              >
                <Icon className="text-white" icon={faLongArrowRight} />
              </button>
            );
          })
        ) : (
          <React.Fragment>
            <Icon className={'text-gray hubspot-email-icon'} icon={faEnvelope} />
            <input
              className="hubspot-input w-full text-sm px-2 leading-none placeholder-gray-600 bg-transparent border-none ring-0"
              data-hubspot-id={hubspotFormId}
              onChange={e => {
                setError(null);
                setEmail(e.target.value);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
              placeholder={placeholderTitle}
              type="email"
              value={email}
            />
            <button
              aria-label="Click to submit email"
              className={
                styleOption === 'secondary'
                  ? `button py-1.5 px-2 bg-white rounded cursor-pointer select-none
                active:translate-y-0.3 active:[box-shadow:0_0px_0_0_#cecece]
                active:border-b-[0px]
                transition-all duration-150 [box-shadow:0_1px_0_0_#cecece]
                border-b-[1px] border-blue-400
              `
                  : 'focus:outline-none py-1.5 px-2 inline-flex items-center justify-center bg-yellow-500 text-white rounded hubspot-form-button'
              }
              onClick={handleSubmit}
              type="button"
            >
              <Icon className={styleOption === 'secondary' ? 'text-gray-800' : 'text-white'} icon={faLongArrowRight} />
            </button>
          </React.Fragment>
        )}
      </div>
      {error && styleOption !== 'secondary' && (
        <div className="p-1">
          <span className="text-red-500 bold">{error}</span>
        </div>
      )}
      {styleOption === 'secondary' && (
        <div className={classnames('error-message-wrapper', { [`error-message-active`]: error })}>
          <span>{error}</span>
        </div>
      )}
    </HubspotFormWrapper>
  );
};
